import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Sparkles, Shield, Zap, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageLightbox } from "@/components/ui/image-lightbox";

import screenshotPt from "@/assets/screenshots/pt/executive-overview.png";
import screenshotEn from "@/assets/screenshots/en/executive-overview.png";

// Use public paths for hero thumbs so they can be preloaded from index.html
const thumbPt = "/img/hero-thumb-pt.jpg";
const thumbEn = "/img/hero-thumb-en.jpg";

export function HeroSection() {
  const { t, i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');
  const thumb = isPt ? thumbPt : thumbEn;
  const full = isPt ? screenshotPt : screenshotEn;

  return (
    <section
      className="relative overflow-hidden py-20 md:py-32"
      style={{ background: "linear-gradient(180deg, #1A1F3C 0%, #0F1828 100%)" }}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_center,_rgba(79,108,255,0.15)_0%,transparent_60%)]" />

      <div className="container relative">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#4F6CFF]/30 bg-[#4F6CFF]/10 px-4 py-2 text-sm font-medium text-[#4F6CFF] shadow-[0_0_20px_rgba(79,108,255,0.15)]">
            <Sparkles className="h-4 w-4" />
            {t("landing.hero.badge")}
          </div>

          <h1 className="mb-6 text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-[56px] leading-tight">
            <span className="block">{t("landing.hero.headline1")}</span>
            <span className="block text-[#4F6CFF]">{t("landing.hero.headline2")}</span>
          </h1>

          <p className="mx-auto mb-10 max-w-2xl text-lg text-white/70 md:text-xl">{t("landing.hero.subheadline")}</p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button size="lg" className="h-12 px-8 text-base bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white" asChild>
              <Link to="/signup">
                {t("landing.hero.primaryCTA")}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-12 px-8 text-base border-white/30 text-white hover:bg-white/10 bg-transparent"
              asChild
            >
              <Link to="/demo">{t("landing.hero.secondaryCTA")}</Link>
            </Button>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-white/50">
            <span className="flex items-center gap-1.5">
              <Shield className="h-4 w-4 text-[#00C896]" />
              {t("landing.hero.trust1")}
            </span>
            <span className="flex items-center gap-1.5">
              <Zap className="h-4 w-4 text-[#00C896]" />
              {t("landing.hero.trust2")}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="h-4 w-4 text-[#00C896]" />
              {t("landing.hero.trust3")}
            </span>
          </div>

          <div className="mt-16 relative mx-auto max-w-3xl">
            <div
              className="rounded-xl border border-white/10 bg-white/5 p-2 shadow-2xl backdrop-blur-sm"
              style={{ transform: "perspective(1000px) rotateX(5deg)" }}
            >
              <div className="rounded-lg bg-[#0F1828] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1A1F3C] border-b border-white/10">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400/60" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400/60" />
                    <div className="w-3 h-3 rounded-full bg-green-400/60" />
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="h-5 rounded-md bg-white/10 max-w-xs mx-auto flex items-center justify-center">
                      <span className="text-[10px] text-white/40">revops.digitais.ai/executive-overview</span>
                    </div>
                  </div>
                </div>
                <div className="aspect-[16/9] overflow-hidden">
                  <ImageLightbox
                    src={thumb}
                    fullSrc={full}
                    alt="RevOps AI Executive Overview"
                    className="w-full h-full object-cover object-top"
                    eager
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
